import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { isEmpty } from "lodash";
import { getSearchObjectives } from "src/queries/objectives.graphql";
import { PAGE_SIZE } from "src/pages/GoalsExplorer/consts";

const useGetObjectives = ({
  objectiveIds = [],
  maxChildDepth = 1,
  includeChildObjectives = false,
  includeParentObjectives = false,
  includeAssociatedTeams = false,
  inputOverrides = {},
  onCompleted,
  handleAppendRootData,
  skip = false,
  rootTypesFilterKey = null,
} = {}) => {
  const paginationRef = useRef({ limit: PAGE_SIZE, offset: 0 });
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const initialLimitAndOffset = isEmpty(objectiveIds)
    ? { limit: PAGE_SIZE, offset: 0 }
    : {};

  const resetLimitAndOffset = useCallback(() => {
    paginationRef.current = { limit: PAGE_SIZE, offset: 0 };
  }, []);

  const setHasMoreAndTotalCount = useCallback(
    (data) => {
      const { hasMore: initialHasMore, totalCount: initialTotalCount } =
        data.searchObjectives || {};
      if (initialHasMore !== undefined) {
        setHasMore(initialHasMore);
      }
      if (initialTotalCount !== undefined) {
        setTotalCount(initialTotalCount);
      }
      resetLimitAndOffset();
    },
    [resetLimitAndOffset]
  );

  // Stable refs to avoid stale closures in reactive data effect.
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;
  const setHasMoreAndTotalCountRef = useRef(setHasMoreAndTotalCount);
  setHasMoreAndTotalCountRef.current = setHasMoreAndTotalCount;

  const queryResult = useQuery(getSearchObjectives, {
    skip,
    variables: {
      input: {
        filter: {
          objectiveIds,
          ...(rootTypesFilterKey ? { rootTypesFilterKey } : {}),
        },
        excludeBaseObjectives: false,
        includeTotalCount: isEmpty(objectiveIds),
        includeChildObjectives,
        includeParentObjectives,
        includeAssociatedTeams,
        maxChildDepth,
        ...initialLimitAndOffset,
        ...inputOverrides,
      },
    },
  });

  // Apollo v4 does not reliably fire onCompleted when passed to useQuery,
  // so we watch returned data reactively via useEffect instead.
  const processedDataRef = useRef(null);
  useEffect(() => {
    const data = queryResult.data;
    if (!data || data === processedDataRef.current) {
      return;
    }
    processedDataRef.current = data;
    if (onCompletedRef.current) {
      onCompletedRef.current(data);
    }
    setHasMoreAndTotalCountRef.current(data);
  }, [queryResult.data]);

  const { fetchMore } = queryResult;

  const handleLoadMore = useCallback(
    ({ includeChildObjectivesParam = true } = {}) => {
      const nextOffset = paginationRef.current.offset + PAGE_SIZE;

      setLoadingMore(true);

      fetchMore({
        variables: {
          input: {
            filter: {
              objectiveIds: [],
              ...(rootTypesFilterKey ? { rootTypesFilterKey } : {}),
            },
            excludeBaseObjectives: false,
            includeChildObjectives: includeChildObjectivesParam,
            includeParentObjectives: false,
            includeTotalCount: true,
            maxChildDepth: 1,
            limit: PAGE_SIZE,
            offset: nextOffset,
          },
        },
      })
        .then((result) => {
          if (result?.data) {
            if (handleAppendRootData) {
              handleAppendRootData(result.data);
            }

            const { hasMore: nextHasMore } = result.data.searchObjectives || {};
            if (nextHasMore !== undefined) {
              setHasMore(nextHasMore);
            }
            paginationRef.current = { limit: PAGE_SIZE, offset: nextOffset };
          }
        })
        .finally(() => {
          setLoadingMore(false);
        });
    },
    [fetchMore, handleAppendRootData, rootTypesFilterKey]
  );

  const totalFetchedCount =
    totalCount > 0
      ? Math.min(paginationRef.current.offset + PAGE_SIZE, totalCount)
      : 0;

  return {
    ...queryResult,
    hasMore,
    totalCount,
    totalFetchedCount,
    loadingMore,
    handleLoadMore,
    resetLimitAndOffset,
  };
};

export default useGetObjectives;
